import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  Info,
  LayoutDashboard,
  Loader2,
  MapPin,
  ShieldAlert,
  Wallet,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { RentAffordabilityReport } from '../../types/rentAffordability';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const verdictTone = (label: string) => {
  const normalized = label.toLowerCase();
  if (normalized.includes('comfortable')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (normalized.includes('tight')) return 'bg-amber-100 text-amber-700 border-amber-200';
  if (normalized.includes('reality') || normalized.includes('severe')) return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const RentAffordabilityDetailPage: React.FC = () => {
  const { affordabilitySlug } = useParams<{ affordabilitySlug: string }>();
  const [data, setData] = useState<RentAffordabilityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const parsed = useMemo(() => {
    const slug = String(affordabilitySlug || '').toLowerCase();
    const match = slug.match(/^how-much-rent-can-i-afford-on-(\d+)-in-([a-z-]+)$/);
    if (!match) return null;
    return {
      slug,
      salary: Number(match[1]),
      county: match[2],
    };
  }, [affordabilitySlug]);

  useEffect(() => {
    if (!parsed?.salary || !parsed?.county) {
      setError('Affordability route parameters are missing.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getRentAffordabilityReport?slug=${encodeURIComponent(
            parsed.slug
          )}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Affordability report not found for this salary and county.');
          return;
        }
        setData(result.data as RentAffordabilityReport);
      } catch (e) {
        setError('Unable to load affordability report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [parsed]);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mb-4" />
        <p className="text-slate-500 font-bold animate-pulse">Calculating rent affordability...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="bg-white rounded-[2rem] p-10 shadow-xl border border-slate-100 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-4">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/affordability"
            className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black hover:bg-emerald-700 transition-all"
          >
            Browse Affordability Reports
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/affordability/${data.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareTitle = data.seo_content.meta_desc;
  const netRatio = (data.calculations.net_annual / data.calculations.gross_annual) * 100;

  return (
    <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-[#f8fafc] min-h-screen font-sans antialiased text-slate-900">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-12 pb-24 overflow-hidden relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] -mr-48 -mt-48" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] -ml-48 -mb-48" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-12 items-start lg:items-center">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-black uppercase tracking-widest mb-6">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                {data.tax_year} RENTSMART.IE RENT AFFORDABILITY
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6 leading-[1.05]">
                {data.seo_content.h1}
              </h1>
              <p className="text-slate-400 text-lg md:text-xl max-w-3xl leading-relaxed mb-8 font-medium">
                {data.sections.hero_verdict}
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  Share Report:
                </span>
                <div className="flex gap-2">
                  <FacebookShareButton url={shareUrl}>
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={shareTitle}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={shareTitle}>
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton url={shareUrl} title={shareTitle}>
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[420px]">
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 shadow-2xl">
                <div className="space-y-6">
                  <div>
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mb-1">
                      35% Max Rent
                    </div>
                    <div className="text-5xl font-black text-emerald-400">
                      {formatCurrency(data.affordability_metrics.max_rent_monthly)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                    <div>
                      <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-1">
                        Net Monthly
                      </div>
                      <div className="text-xl font-bold">
                        {formatCurrency(data.calculations.net_monthly)}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-1">
                        Post-Rent
                      </div>
                      <div className="text-xl font-bold">
                        {formatCurrency(data.affordability_metrics.post_rent_income)}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">
                      Net Retention Rate
                    </p>
                    <p className="text-sm font-bold text-white">{netRatio.toFixed(1)}% of gross remains after deductions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 -mt-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            <section className="bg-white rounded-[2.5rem] mt-20 shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-3 mb-10">
                  <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600">
                    <LayoutDashboard className="h-6 w-6" />
                  </div>
                  <h2 className="text-3xl font-black text-slate-900">Take-Home Breakdown ({data.tax_year})</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Gross Annual</p>
                    <p className="text-2xl font-black text-slate-900">{formatCurrency(data.calculations.gross_annual)}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-1">Net Monthly</p>
                    <p className="text-2xl font-black text-emerald-900">{formatCurrency(data.calculations.net_monthly)}</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    ['Income Tax (PAYE)', data.calculations.paye],
                    ['USC', data.calculations.usc],
                    ['PRSI', data.calculations.prsi],
                    ['Total Deductions', data.calculations.total_deductions],
                    ["Renter's Credit Effective", data.affordability_metrics.renters_credit_monthly_effective],
                  ].map(([label, value]) => (
                    <div
                      key={String(label)}
                      className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100"
                    >
                      <span className="font-bold text-slate-600 text-sm">{label}</span>
                      <span className="font-black text-slate-900">{formatCurrency(Number(value))}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-emerald-600 rounded-3xl p-8 text-white">
                  <p className="text-emerald-200 text-xs font-black uppercase tracking-[0.2em] mb-2">
                    Method Note
                  </p>
                  <p className="text-sm font-semibold leading-relaxed">{data.sections.take_home_breakdown_note}</p>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
              <h2 className="text-3xl font-black text-slate-900 mb-6">Area Realism in {data.county_name}</h2>
              <div className="flex items-center gap-3 mb-5">
                <span
                  className={`inline-flex px-3 py-1 rounded-full border text-xs font-black uppercase tracking-wider ${verdictTone(
                    data.county_market.badge
                  )}`}
                >
                  {data.county_market.badge}
                </span>
                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Reality Signal</p>
              </div>

              <p className="text-slate-700 leading-relaxed mb-5">{data.sections.area_realism}</p>

              <div className="grid sm:grid-cols-2 gap-4 mb-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">County Median 1-Bed</p>
                  <p className="text-2xl font-black text-slate-900">{formatCurrency(data.county_market.median_rent_1bed)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Gap vs Your Rent Cap</p>
                  <p className="text-2xl font-black text-slate-900">{formatCurrency(data.county_market.rent_gap_to_median)}</p>
                </div>
              </div>

              {data.county_market.commuter_pivot ? (
                <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
                  <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-sky-700 mb-2">
                    <MapPin className="h-4 w-4" /> Commuter Pivot
                  </div>
                  <p className="text-sm text-sky-900 font-semibold leading-relaxed">{data.sections.commuter_pivot}</p>
                </div>
              ) : null}
            </section>

            <section className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
              <h2 className="text-3xl font-black text-slate-900 mb-6">Lifestyle Survival Calculator</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Post-Rent Income</p>
                  <p className="text-2xl font-black text-slate-900">{formatCurrency(data.affordability_metrics.post_rent_income)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Utilities</p>
                  <p className="text-2xl font-black text-slate-900">{formatCurrency(data.lifestyle_survival.utilities_estimate)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Groceries</p>
                  <p className="text-2xl font-black text-slate-900">{formatCurrency(data.lifestyle_survival.groceries_estimate)}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-1">Remaining</p>
                  <p className="text-2xl font-black text-emerald-900">{formatCurrency(data.lifestyle_survival.post_fixed_cost_income)}</p>
                </div>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
                <p className="text-sm text-amber-900 font-semibold leading-relaxed">{data.sections.survival_verdict}</p>
              </div>
            </section>

            {data.ai_enrichment ? (
              <section className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm space-y-6">
                <h2 className="text-3xl font-black text-slate-900">Renter Strategy</h2>
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">
                      Verdict Context
                    </p>
                    <p className="text-sm font-semibold text-emerald-900 leading-relaxed">
                      {data.ai_enrichment.verdict_context}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-sky-700 mb-2">
                      Renter Profile Fit
                    </p>
                    <p className="text-sm font-semibold text-sky-900 leading-relaxed">
                      {data.ai_enrichment.renter_profile_fit}
                    </p>
                  </div>
                  <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">
                      Market Pressure Commentary
                    </p>
                    <p className="text-sm font-semibold text-slate-700 leading-relaxed">
                      {data.ai_enrichment.market_pressure_commentary}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-3">
                      Negotiation Tips
                    </p>
                    <ul className="space-y-2 text-sm font-semibold text-amber-900">
                      {data.ai_enrichment.negotiation_tips.map((tip, idx) => (
                        <li key={`negotiation-${idx}`}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-violet-200 bg-violet-50 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-violet-700 mb-3">
                      Savings Moves
                    </p>
                    <ul className="space-y-2 text-sm font-semibold text-violet-900">
                      {data.ai_enrichment.savings_moves.map((move, idx) => (
                        <li key={`savings-${idx}`}>• {move}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {data.ai_enrichment.commuter_alternatives?.length ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-3">
                      Alternative County Plays
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {data.ai_enrichment.commuter_alternatives.map((item, idx) => (
                        <div key={`alternative-${idx}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-sm font-black text-slate-900 mb-1">{item.county}</p>
                          <p className="text-xs font-semibold text-slate-600 leading-relaxed">{item.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}

            <PseoEmailCapture
              pageType="rent-affordability"
              pageSlug={data.slug}
              locationName={data.county_name}
              contextHeadline={`Get ${data.county_name} Rent Updates Before Prices Shift`}
              contextReason={`Based on your affordability range (${formatCurrency(
                data.affordability_metrics.max_rent_monthly
              )}/month), we will send renter-focused alerts on Irish market rent movement, affordability report updates, and practical actions for ${data.county_name}.`}
            />

            <section className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
              <h2 className="text-3xl font-black text-slate-900 mb-8">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {data.seo_content.faq.map((item, idx) => (
                  <details
                    key={`${item.question}-${idx}`}
                    className="group bg-slate-50 rounded-2xl overflow-hidden transition-all"
                  >
                    <summary className="flex justify-between items-center p-6 cursor-pointer font-bold text-slate-800 list-none">
                      {item.question}
                      <span className="bg-white p-1 rounded-full group-open:rotate-180 transition-transform">
                        <ArrowRight className="h-4 w-4 rotate-90" />
                      </span>
                    </summary>
                    <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-white/50 pt-4">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section className="bg-amber-50 rounded-[2.5rem] border border-amber-200 p-8 md:p-10 shadow-sm">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-black text-amber-900 mb-3">Important Disclaimer</h2>
                  <p className="text-amber-900/90 text-sm leading-relaxed mb-3">
                    Figures are directional estimates only. They do not account for your full personal
                    circumstances, credits, liabilities or lender-specific underwriting criteria.
                  </p>
                  <p className="text-amber-900/90 text-sm leading-relaxed">
                    RentSmart does not provide tax, financial, mortgage, legal or investment advice.
                    Confirm decisions with qualified professionals.
                  </p>
                </div>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-8 space-y-6">
              <div className="bg-emerald-700 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-emerald-900/20">
                <Wallet className="h-12 w-12 mb-6 opacity-50 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-black mb-2">Rent Listings</h3>
                <p className="text-emerald-100/80 text-sm font-medium mb-8 leading-relaxed">
                  Jump to current rentals in {data.county_name} near your affordability threshold.
                </p>
                <a
                  href={data.ctas.rent.path}
                  className="no-underline flex items-center justify-between bg-white text-emerald-700 py-4 px-6 rounded-2xl font-black hover:bg-emerald-50 transition-all"
                >
                  {data.ctas.rent.label} <ArrowRight className="h-5 w-5" />
                </a>
              </div>

              {data.ctas.similar_county ? (
                <div className="bg-sky-800 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-sky-900/20">
                  <MapPin className="h-12 w-12 mb-6 opacity-50 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-black mb-2">Commuter Pivot</h3>
                  <p className="text-sky-100/80 text-sm font-medium mb-8 leading-relaxed">
                    If this county is tight, test a nearby county with the same salary.
                  </p>
                  <Link
                    to={data.ctas.similar_county.path}
                    className="no-underline flex items-center justify-between bg-white text-sky-800 py-4 px-6 rounded-2xl font-black hover:bg-sky-50 transition-all"
                  >
                    {data.ctas.similar_county.label} <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              ) : null}

              <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
                <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                  <CheckCircle2 className="text-emerald-500 h-5 w-5" /> Next Steps
                </h3>
                <div className="space-y-6">
                  {[
                    'Use the rent cap as your maximum filter, not your default spend target.',
                    'Prioritize counties where median rent is near your 35% threshold.',
                    "Keep renter's credit and monthly fixed costs in your cashflow model.",
                  ].map((step, i) => (
                    <div key={step} className="flex gap-4">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-black text-xs text-slate-400">
                        0{i + 1}
                      </span>
                      <p className="text-sm font-bold text-slate-600 leading-tight pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Link
                to={data.ctas.salary_after_tax.path}
                className="no-underline flex items-center justify-between w-full py-4 px-6 rounded-2xl border border-slate-200 text-slate-600 font-black hover:border-emerald-500 hover:text-emerald-600 transition-all"
              >
                View Salary-After-Tax Page <ArrowRight className="h-4 w-4" />
              </Link>

              <Link
                to="/affordability"
                className="flex items-center justify-center gap-2 w-full py-5 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 font-black uppercase text-xs tracking-[0.2em] hover:border-emerald-500 hover:text-emerald-600 transition-all"
              >
                Try Another Salary <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-12 bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">Share This Affordability Report</h2>
              <p className="text-slate-600 text-sm">
                Share this page with someone comparing salary, rent limits, and county trade-offs.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <FacebookShareButton url={shareUrl}>
                <FacebookIcon size={36} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareTitle}>
                <TwitterIcon size={36} round />
              </TwitterShareButton>
              <WhatsappShareButton url={shareUrl} title={shareTitle}>
                <WhatsappIcon size={36} round />
              </WhatsappShareButton>
              <LinkedinShareButton url={shareUrl} title={shareTitle}>
                <LinkedinIcon size={36} round />
              </LinkedinShareButton>
            </div>
          </div>
        </section>


        <section className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5" />
            <p className="text-sm text-amber-900/90 font-semibold leading-relaxed">
              Use this estimate as planning guidance only. Always verify final costs and eligibility
              with qualified professionals.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RentAffordabilityDetailPage;
